import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FadeUpAnimation from '../../components/base/FadeUpAnimation';
import { useSEO, getSiteUrl } from '../../utils/seo';

const ServicesPage = () => {
  useSEO({
    title: 'サービス一覧 — 生成AI・政策・地域活性化支援 | 2b LLC',
    description: '2b LLCのサービス一覧。生成AIコンサルティング、政治・政策支援、地域活性化プロデュース、不動産・空間プロデュース、秘書業代行・バックオフィス支援、ライティングなど、想いを形にする6つの事業をご紹介します。',
    keywords: '生成AI,AIコンサルティング,政策支援,地域活性化,不動産,秘書業,ライティング,2b LLC',
    canonical: getSiteUrl('/services'),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '2b LLCのサービス',
      description: '生成AI、政策、地域活性化を通じて、想いを形にする支援サービス',
      provider: {
        '@type': 'Organization',
        name: '合同会社2b',
        url: getSiteUrl('/')
      },
      areaServed: {
        '@type': 'Country',
        name: '日本'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'サービス一覧',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '生成AIコンサルティング',
              description: 'AIを使いこなすのではなく、AIと共に考える時代へ。人間らしさを拡張するAI活用を提案・支援します。'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '政治・政策支援',
              description: '社会をよくする想いを、実現するために。候補者・議員・団体等の活動を、裏方として支えます。'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '地域活性化プロデュース',
              description: 'まちの呼吸を感じ、呼吸に合わせてゆるやかに動かす。観光・空き家・イベント・飲食、地域の現場と共に考えます。'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '不動産・空間プロデュース',
              description: '場でもあり関係のうつわでもあるのが建物です。人が集い、物語が生まれる空間をデザインします。'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '秘書業代行・バックオフィス支援',
              description: '誰かの仕事を軽くする。そんな静かな力を、チームの一員として提供します。'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'ライティング',
              description: '言葉は想いを運ぶための道具。伝わるより、届く文章を。'
            }
          }
        ]
      }
    }
  });

  return (
    <div className="min-h-screen bg-[#F6F3EB]">
      <Header />
      
      <main className="pt-32 pb-20">
        {/* ---------- ヒーローセクション ---------- */}
        <section
          className="py-32 bg-[#F6F3EB] relative overflow-hidden"
          style={{
            backgroundImage:
              'url(https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/e2213d932679368b2c76646fe04e2a17.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-[#F6F3EB]/75"></div>
          <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
            <div className="text-center mb-20">
              <FadeUpAnimation duration={1200}>
                <img 
                  src="https://static.readdy.ai/image/721199a06948bc0907bde1e591c4b4e9/191bb64e22d504c48b73a6bcbfddf67c.png"
                  alt="2b LLC"
                  className="w-[360px] h-auto mx-auto mb-12 object-contain"
                />
              </FadeUpAnimation>

              <FadeUpAnimation delay={150} duration={1200}>
                <h1
                  className="text-2xl md:text-3xl font-semibold text-[#4A4A48] mb-4"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Services
                </h1>
              </FadeUpAnimation>

              <FadeUpAnimation delay={300} duration={1200}>
                <p
                  className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                  style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                >
                  私たちは、行動をためらう誰かの隣で、<br />
                  静かに段取りを整え、仕組みを作り、<br />
                  そして言葉を紡ぎます。
                </p>
                <p
                  className="text-base md:text-xl leading-[1.75] text-[#4A4A48]"
                  style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                >
                  ここで紹介する6つの事業は、<br />
                  それぞれ違う形で "伴走" を行うものです。
                </p>
              </FadeUpAnimation>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-6">
          {/* ---------- サービス一覧 ---------- */}
          <section className="space-y-32">
            {/* 生成AIコンサルティング */}
            <div className="transition-all duration-500">
                <FadeUpAnimation delay={150} duration={1200}>
                  <section className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="p-8 md:p-20 flex flex-col justify-center">
                        <h2
                          className="text-sm md:text-lg font-semibold text-[#4A4A48] mb-6 pb-2 border-b border-[#C8C6C3]/40"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          生成AIコンサルティング
                        </h2>
                        <p
                          className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          AIを使いこなすのではなく、<br />
                          AIと共に考える時代へ。<br />
                          「人間らしさを拡張するAI活用」を提案・支援します。
                        </p>
                        <ul className="space-y-3 mb-8">
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>業務プロセスへのAI導入設計</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>プロンプトエンジニアリング研修</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>生成AI活用ガイドライン策定</span>
                          </li>
                        </ul>
                        <p
                          className="text-sm md:text-xl italic text-[#4A4A48]/70 leading-[1.75]"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "技術ではなく、人の可能性を広げる"
                        </p>
                      </div>
                      <div className="group">
                        <img
                          alt="生成AIコンサルティング"
                          className="w-full h-64 md:h-96 object-cover object-top opacity-80 rounded-2xl shadow-sm group-hover:scale-103 transition-transform duration-500 ease-in-out"
                          src="https://public.readdy.ai/ai/img_res/b58309d104a88be440b1f0a560fd994f.jpg"
                        />
                      </div>
                    </div>
                  </section>
                </FadeUpAnimation>
              </div>

            {/* 政治・政策支援 */}
            <div className="transition-all duration-500">
                <FadeUpAnimation delay={300} duration={1200}>
                  <section className="bg-[#F6F3EB] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0 md:grid-flow-col-dense">
                      <div className="p-8 md:p-20 flex flex-col justify-center md:col-start-2">
                        <h2
                          className="text-sm md:text-lg font-semibold text-[#4A4A48] mb-6 pb-2 border-b border-[#C8C6C3]/40"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          政治・政策支援
                        </h2>
                        <p
                          className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          社会をよくする"想い"を、実現するために。<br />
                          候補者・議員・団体等の活動を、裏方として支えます。
                        </p>
                        <ul className="space-y-3 mb-8">
                          <li
                            className="text-[#4A4A48] leading-[1.7] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>政策企画書・演説原稿の作成</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.7] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>政治活動方針の立案サポート</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.7] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>地域課題リサーチ・資料作成</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.7] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>スピーチライティング</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.7] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>広報戦略</span>
                          </li>
                        </ul>
                        <p
                          className="text-sm md:text-xl italic text-[#4A4A48]/70 leading-[1.75]"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "想いを伝える技術を、誠実に"
                        </p>
                      </div>
                      <div className="md:col-start-1 group">
                        <img
                          alt="政治・政策支援"
                          className="w-full h-64 md:h-96 object-cover object-top opacity-80 rounded-2xl shadow-sm group-hover:scale-103 transition-transform duration-500 ease-in-out"
                          src="https://readdy.ai/api/search-image?query=Professional%20government%20building%20or%20meeting%20room%20with%20warm%20natural%20lighting%2C%20documents%20and%20papers%20on%20wooden%20desk%2C%20peaceful%20political%20workspace%20clean%20minimalist%20design%20soft%20beige%20and%20white%20tones%20democratic%20atmosphere%20policy%20documents%20thoughtful%20planning%20environment%20serene%20professional%20setting&width=600&height=400&seq=political-support&orientation=landscape"
                        />
                      </div>
                    </div>
                  </section>
                </FadeUpAnimation>
              </div>

            {/* 地域活性化プロデュース */}
            <div className="transition-all duration-500">
                <FadeUpAnimation delay={450} duration={1200}>
                  <section className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="p-8 md:p-20 flex flex-col justify-center">
                        <h2
                          className="text-sm md:text-lg font-semibold text-[#4A4A48] mb-6 pb-2 border-b border-[#C8C6C3]/40"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          地域活性化プロデュース
                        </h2>
                        <p
                          className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "まちの呼吸"を感じ、呼吸に合わせてゆるやかに動かす。<br />
                          観光・空き家・イベント・飲食、地域の現場と共に考えます。
                        </p>
                        <ul className="space-y-3 mb-8">
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>地域ブランディング</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>SNS発信</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>空き物件／民泊プロジェクト</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>地域イベント・マルシェ企画</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>自治体・商店街との連携支援</span>
                          </li>
                        </ul>
                        <p
                          className="text-sm md:text-xl italic text-[#4A4A48]/70 leading-[1.75]"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "地元の声を、かたちに"
                        </p>
                      </div>
                      <div className="group">
                        <img
                          alt="地域活性化プロデュース"
                          className="w-full h-64 md:h-96 object-cover object-top opacity-80 rounded-2xl shadow-sm group-hover:scale-103 transition-transform duration-500 ease-in-out"
                          src="https://readdy.ai/api/search-image?query=Charming%20local%20community%20street%20scene%20with%20traditional%20Japanese%20architecture%2C%20small%20shops%20and%20cafes%2C%20people%20walking%20peacefully%20warm%20afternoon%20lighting%20community%20gathering%20spaces%20local%20market%20atmosphere%20revitalized%20neighborhood%20gentle%20urban%20planning%20human-scale%20development%20cozy%20town%20atmosphere&width=600&height=400&seq=community-revitalization&orientation=landscape"
                        />
                      </div>
                    </div>
                  </section>
                </FadeUpAnimation>
              </div>

            {/* 不動産・空間プロデュース */}
            <div className="transition-all duration-500">
                <FadeUpAnimation delay={600} duration={1200}>
                  <section className="bg-[#F6F3EB] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0 md:grid-flow-col-dense">
                      <div className="p-8 md:p-20 flex flex-col justify-center md:col-start-2">
                        <h2
                          className="text-sm md:text-lg font-semibold text-[#4A4A48] mb-6 pb-2 border-b border-[#C8C6C3]/40"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          不動産・空間プロデュース
                        </h2>
                        <p
                          className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "場"でもあり"関係のうつわ"でもあるのが建物です。<br />
                          人が集い、物語が生まれる空間をデザインします。
                        </p>
                        <ul className="space-y-3 mb-8">
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>収益不動産の調査・運営支援</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>利活用プラン設計</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>リノベーション</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>民泊／店舗開発プロデュース</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>地域不動産データリサーチ</span>
                          </li>
                        </ul>
                        <p
                          className="text-sm md:text-xl italic text-[#4A4A48]/70 leading-[1.75]"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "空間を通して、人をつなぐ"
                        </p>
                      </div>
                      <div className="md:col-start-1 group">
                        <img
                          alt="不動産・空間プロデュース"
                          className="w-full h-64 md:h-96 object-cover object-top opacity-80 rounded-2xl shadow-sm group-hover:scale-103 transition-transform duration-500 ease-in-out"
                          src="https://readdy.ai/api/search-image?query=Beautiful%20renovated%20space%20with%20natural%20lighting%2C%20modern%20interior%20design%20mixed%20with%20traditional%20elements%2C%20cozy%20cafe%20or%20co-working%20space%20atmosphere%20people%20gathering%20and%20connecting%20warm%20wood%20tones%20soft%20textures%20inviting%20community%20space%20architectural%20renovation%20showcase%20human-centered%20design%20peaceful%20social%20environment&width=600&height=400&seq=real-estate-space&orientation=landscape"
                        />
                      </div>
                    </div>
                  </section>
                </FadeUpAnimation>
              </div>

            {/* 秘書業代行・バックオフィス支援 */}
            <div className="transition-all duration-500">
                <FadeUpAnimation delay={750} duration={1200}>
                  <section className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="p-8 md:p-20 flex flex-col justify-center">
                        <h2
                          className="text-sm md:text-lg font-semibold text-[#4A4A48] mb-6 pb-2 border-b border-[#C8C6C3]/40"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          秘書業代行・バックオフィス支援
                        </h2>
                        <p
                          className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          「誰かの仕事を軽くする」。<br />
                          そんな静かな力を、チームの一員として提供します。
                        </p>
                        <ul className="space-y-3 mb-8">
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>スケジュール／文書管理</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>調査／議事録／資料作成</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>メール・SNS・業務連絡代行</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>経営補佐／秘書業務全般</span>
                          </li>
                        </ul>
                        <p
                          className="text-sm md:text-xl italic text-[#4A4A48]/70 leading-[1.75]"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "見えないところで、整える"
                        </p>
                      </div>
                      <div className="group">
                        <img
                          alt="秘書業代行・バックオフィス支援"
                          className="w-full h-64 md:h-96 object-cover object-top opacity-80 rounded-2xl shadow-sm group-hover:scale-103 transition-transform duration-500 ease-in-out"
                          src="https://readdy.ai/api/search-image?query=Organized%20office%20workspace%20with%20neat%20documents%2C%20calendar%20and%20scheduling%20tools%2C%20professional%20administrative%20environment%20clean%20desk%20setup%20soft%20natural%20lighting%20minimalist%20organization%20system%20supportive%20work%20atmosphere%20efficient%20workflow%20visualization%20peaceful%20productivity%20space%20warm%20professional%20setting&width=600&height=400&seq=secretary-support&orientation=landscape"
                        />
                      </div>
                    </div>
                  </section>
                </FadeUpAnimation>
              </div>

            {/* ライティング */}
            <div className="transition-all duration-500">
                <FadeUpAnimation delay={900} duration={1200}>
                  <section className="bg-[#F6F3EB] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-0 md:grid-flow-col-dense">
                      <div className="p-8 md:p-20 flex flex-col justify-center md:col-start-2">
                        <h2
                          className="text-sm md:text-lg font-semibold text-[#4A4A48] mb-6 pb-2 border-b border-[#C8C6C3]/40"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          ライティング
                        </h2>
                        <p
                          className="text-base md:text-xl leading-[1.75] text-[#4A4A48] mb-8"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "言葉"は想いを運ぶための道具。<br />
                          伝わるより、届く文章を。
                        </p>
                        <ul className="space-y-3 mb-8">
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>企業／団体のメッセージ制作</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>コラム・インタビュー・代表挨拶文作成</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>Web／パンフレットライティング</span>
                          </li>
                          <li
                            className="text-[#4A4A48] leading-[1.75] flex items-start text-sm md:text-lg"
                            style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                          >
                            <i className="ri-check-line text-[#AFC5C2] mr-3 mt-1 flex-shrink-0"></i>
                            <span>SNS発信文・キャッチコピー制作</span>
                          </li>
                        </ul>
                        <p
                          className="text-sm md:text-xl italic text-[#4A4A48]/70 leading-[1.75]"
                          style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
                        >
                          "言葉に、あたたかさを"
                        </p>
                      </div>
                      <div className="md:col-start-1 group">
                        <img
                          alt="文章・広報コンテンツ制作"
                          className="w-full h-64 md:h-96 object-cover object-top opacity-80 rounded-2xl shadow-sm group-hover:scale-103 transition-transform duration-500 ease-in-out"
                          src="https://readdy.ai/api/search-image?query=Writer%20workspace%20with%20notebooks%2C%20pens%2C%20and%20papers%20scattered%20thoughtfully%20warm%20natural%20lighting%20from%20window%20creative%20writing%20environment%20soft%20textures%20warm%20colors%20inspiring%20literary%20atmosphere%20peaceful%20creative%20space%20handwritten%20notes%20drafts%20cozy%20writing%20nook%20artistic%20composition&width=600&height=400&seq=content-writing&orientation=landscape"
                        />
                      </div>
                    </div>
                  </section>
                </FadeUpAnimation>
              </div>
          </section>

          {/* ---------- CTA ---------- */}
          <section className="py-24 bg-[#4A4A48] rounded-2xl text-center mt-32 mb-20">
            <FadeUpAnimation delay={150} duration={1200}>
              <h2
                className="text-base md:text-xl font-semibold text-white mb-8"
                style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
              >
                想いを、仕組みに変えるお手伝いをします。
              </h2>
              <p
                className="text-base md:text-xl text-white/90 mb-12 max-w-2xl mx-auto leading-[1.75]"
                style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
              >
                「やりたいけど、どこから始めていいか分からない」<br />
                そんな時こそ、私たちの出番です。<br />
                まだ言葉にならない段階でも、お話を聞かせて下さい。
              </p>
              <a 
                href="mailto:tobe26.2b@gmail.com"
                className="inline-block bg-white text-[#4A4A48] px-6 py-3 rounded-lg font-medium hover:bg-[#5B5A57] hover:text-white transition-colors duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Zen Kaku Gothic Soft", sans-serif' }}
              >
                📩 ご相談・お問い合わせはこちら
              </a>
            </FadeUpAnimation>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ServicesPage;
